import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { useImmoTheme } from '../../../../lib/immobilien/theme';

/** Formular-Bausteine des E-Mail-Bereichs (Prototyp: Feld 44 px, Radius 14, --bg mit Linie). */
export const FELD: React.CSSProperties = {
  width: '100%', minHeight: 44, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none',
  background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 14, fontWeight: 600, color: 'var(--ink)', fontFamily: 'inherit',
};

export const MEHRZEILIG: React.CSSProperties = { ...FELD, height: 'auto', padding: '12px 14px', lineHeight: 1.5, resize: 'vertical' };

export const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };

export const HINWEIS: React.CSSProperties = { fontSize: 12, color: 'var(--ink3)', lineHeight: 1.5 };

/** Glas-Karte mit Titelzeile (Einstellungen, Automatische Aktionen). */
export function Karte({ titel, untertitel, aktion, children, kleinTitel = false }: {
  titel: React.ReactNode; untertitel?: React.ReactNode; aktion?: React.ReactNode; children?: React.ReactNode; kleinTitel?: boolean;
}) {
  return (
    <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 24, padding: 18, gap: 12 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
        <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
          <span style={kleinTitel
            ? { fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }
            : { fontSize: 15, fontWeight: 800 }}>{titel}</span>
          {untertitel && <span style={HINWEIS}>{untertitel}</span>}
        </span>
        {aktion}
      </div>
      {children}
    </section>
  );
}

export function Feld({ label, children, hinweis }: { label: string; children: React.ReactNode; hinweis?: React.ReactNode }) {
  return (
    <label className="flex flex-col min-w-0" style={{ gap: 6 }}>
      <span style={LABEL}>{label}</span>
      {children}
      {hinweis && <span style={HINWEIS}>{hinweis}</span>}
    </label>
  );
}

/** Natives Select im Token-Stil; Optionsliste folgt dem Theme (auch in Portalen außerhalb .immo-flaeche). */
export function WahlFeld({ wert, onChange, children, disabled, ariaLabel, hoehe = 44 }: {
  wert: string; onChange: (v: string) => void; children: React.ReactNode; disabled?: boolean; ariaLabel?: string; hoehe?: number;
}) {
  const [theme] = useImmoTheme();
  return (
    <span className="relative flex min-w-0">
      <select value={wert} onChange={e => onChange(e.target.value)} disabled={disabled} aria-label={ariaLabel} className="w-full min-w-0 cursor-pointer"
        style={{ ...FELD, minHeight: hoehe, height: hoehe, appearance: 'none', WebkitAppearance: 'none', padding: '0 34px 0 12px', background: 'var(--fill2)', boxShadow: 'none', fontWeight: 700, fontSize: 13, colorScheme: theme, opacity: disabled ? 0.5 : 1 }}>
        {children}
      </select>
      <CaretDown size={12} weight="bold" color="var(--ink3)" className="absolute pointer-events-none" style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }} />
    </span>
  );
}

/** Zwei bis drei Optionen nebeneinander (z. B. Visuell / HTML). */
export function Umschalter<K extends string>({ optionen, wert, onChange, hoehe = 28 }: {
  optionen: readonly { key: K; label: string; icon?: React.ReactNode }[]; wert: K; onChange: (k: K) => void; hoehe?: number;
}) {
  return (
    <div role="radiogroup" className="flex" style={{ padding: 3, borderRadius: 11, background: 'var(--fill2)' }}>
      {optionen.map(o => (
        <button key={o.key} type="button" role="radio" aria-checked={wert === o.key} onClick={() => onChange(o.key)}
          className="flex items-center cursor-pointer"
          style={{ height: hoehe, padding: '0 12px', gap: 5, borderRadius: 9, border: 'none', fontSize: 12, fontWeight: 700,
            background: wert === o.key ? 'var(--bg)' : 'transparent', color: wert === o.key ? 'var(--ink)' : 'var(--ink2)',
            boxShadow: wert === o.key ? '0 2px 6px -2px var(--shade)' : 'none' }}>
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}
